(() => {
  if (customElements.get('gold-price-ticker')) return;
  const memory = new Map();
  const pending = new Map();
  const numeric = (value) => typeof value === 'number' && Number.isFinite(value);

  class GoldPriceTicker extends HTMLElement {
    connectedCallback() {
      this.primary = this.querySelector('[data-primary]');
      this.track = this.querySelector('.price-ticker__track');
      this.viewport = this.querySelector('.price-ticker__viewport');
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => this.repeat();
      this.motionPreference.addEventListener('change', this.onMotionChange);
      this.currency = ['USD', 'EUR', 'GBP', 'AUD', 'CAD'].includes(this.dataset.currency) ? this.dataset.currency : 'USD';
      this.formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.onVisibility = () => {
        this.syncPlayback();
        if (!document.hidden) this.refresh();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.observer = new ResizeObserver(() => this.repeat());
      this.observer.observe(this.querySelector('.price-ticker__viewport'));
      this.observer.observe(this.primary);
      this.refresh();
      this.timer = setInterval(() => { if (!document.hidden) this.refresh(); }, 60000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.observer?.disconnect();
      this.animation?.cancel();
      this.animation = null;
      this.motionPreference?.removeEventListener('change', this.onMotionChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    repeat() {
      const width = this.primary.getBoundingClientRect().width;
      if (!width) {
        this.animation?.cancel();
        this.animation = null;
        this.track.querySelectorAll('[data-copy]').forEach((node) => node.remove());
        return;
      }
      // Keep at least one full cycle beyond the viewport, including at the seam.
      const count = this.motionPreference.matches ? 0 : Math.ceil(this.viewport.clientWidth / width);
      const markup = this.primary.innerHTML;
      if (markup !== this.copyMarkup || count !== this.copyCount) {
        this.track.querySelectorAll('[data-copy]').forEach((node) => node.remove());
        const copies = document.createDocumentFragment();
        for (let i = 0; i < count; i++) {
          const copy = this.primary.cloneNode(true);
          copy.removeAttribute('data-primary');
          copy.setAttribute('data-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
          copies.append(copy);
        }
        this.track.append(copies);
        this.copyMarkup = markup;
        this.copyCount = count;
      }
      this.updateAnimation(width);
    }

    updateAnimation(width) {
      if (this.motionPreference.matches) {
        this.animation?.cancel();
        this.animation = null;
        return;
      }
      const speed = Math.min(200, Math.max(10, Number(this.dataset.scrollSpeed) || 40));
      if (this.animation && this.cycleWidth === width && this.scrollSpeed === speed) return;
      // Preserve the travelled pixel offset when text width or speed changes.
      const offset = this.animation
        ? ((Number(this.animation.currentTime) || 0) * this.scrollSpeed / 1000) % this.cycleWidth
        : 0;
      this.animation?.cancel();
      this.cycleWidth = width;
      this.scrollSpeed = speed;
      this.animation = this.track.animate([
        { transform: 'translate3d(0, 0, 0)' },
        { transform: `translate3d(${-width}px, 0, 0)` }
      ], { duration: width / speed * 1000, iterations: Infinity, easing: 'linear' });
      this.animation.currentTime = (offset % width) / speed * 1000;
      this.syncPlayback();
    }

    syncPlayback() {
      if (!this.animation) return;
      if (document.hidden) this.animation.pause();
      else if (this.animation.playState !== 'running') this.animation.play();
    }

    priceUrl() {
      const url = new URL(this.dataset.proxyUrl || 'https://price-ticker.theme-coin-admin.workers.dev/prices');
      url.searchParams.set('currency', this.currency);
      return url;
    }

    cachedPrices() {
      const key = `gold-price-ticker:prices:v2:${this.priceUrl().href}`;
      let cached = memory.get(key);
      if (!cached) {
        try { cached = JSON.parse(localStorage.getItem(key)); } catch (_) { /* Storage may be blocked. */ }
      }
      if (!cached?.items || !numeric(cached.cachedAt) || cached.cachedAt > Date.now()) return null;
      return { ...cached, stale: Boolean(cached.stale || Date.now() - cached.cachedAt >= 60000) };
    }

    async prices() {
      const url = this.priceUrl();
      const key = `gold-price-ticker:prices:v2:${url.href}`;
      let cached = memory.get(key);
      if (!cached) {
        try { cached = JSON.parse(localStorage.getItem(key)); } catch (_) { /* Storage may be blocked. */ }
      }
      const now = Date.now();
      if (cached?.items && numeric(cached.cachedAt) && cached.cachedAt <= now && now - cached.cachedAt < 60000) return cached;
      if (pending.has(key)) return pending.get(key);
      const request = (async () => {
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(15000), cache: 'no-store' });
          if (!response.ok) throw new Error('Price request failed');
          const data = await response.json();
          if (!data.ok || !data.items || data.currency !== this.currency) throw new Error('Invalid prices');
          const result = { ...data, cachedAt: now };
          memory.set(key, result);
          try { localStorage.setItem(key, JSON.stringify(result)); } catch (_) { /* Use memory cache. */ }
          return result;
        } catch (_) {
          return cached?.items ? { ...cached, stale: true } : null;
        } finally {
          pending.delete(key);
        }
      })();
      pending.set(key, request);
      return request;
    }

    quote(metal, data) {
      const item = data?.items?.[metal];
      if (!item || item.symbol !== metal || item.currency !== this.currency || !numeric(item.price) || item.price <= 0) return null;
      return { ...item, stale: Boolean(data.stale || item.stale) };
    }

    manualQuote(item) {
      const parse = (value) => /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test((value || '').trim()) ? Number(value) : NaN;
      const price = parse(item.dataset.manualPrice);
      const change = parse(item.dataset.manualChange);
      return numeric(price) && price >= 0 && numeric(change) && change > -100
        ? { price, changePercent: change, change: price - price / (1 + change / 100), manual: true } : null;
    }

    async refresh() {
      if (this.loading) return;
      this.loading = true;
      try {
        // Paint the last known prices before waiting for the network on reload.
        this.renderPrices(this.cachedPrices());
        const items = Array.from(this.primary.querySelectorAll('[data-metal]'));
        const data = items.some((item) => item.dataset.manual !== 'true') ? await this.prices() : null;
        this.renderPrices(data);
      } finally {
        this.loading = false;
      }
    }

    renderPrices(data) {
        const items = Array.from(this.primary.querySelectorAll('[data-metal]'));
        const results = items.map((item) => {
          const manual = item.dataset.manual === 'true';
          const quote = manual ? this.manualQuote(item) : this.quote(item.dataset.metal, data);
          if (!this.isConnected) return quote;
          item.toggleAttribute('data-ready', Boolean(quote));
          item.querySelector('[data-price]').textContent = quote ? this.formatter.format(quote.price) : '';
          item.title = quote ? `Giá / troy ounce · ${this.currency} · ${new Date(quote.updatedAt || quote.priceAt).toLocaleString()}${quote.stale ? ' · Chưa cập nhật được giá mới' : ''}` : 'Chưa lấy được giá';
          if (manual) item.title = quote ? 'Giá thủ công / troy ounce · ' + this.currency : 'Giá thủ công không hợp lệ';
          const change = item.querySelector('[data-change]');
          if (change) {
            const value = quote?.change;
            const percent = quote?.changePercent;
            change.dataset.direction = numeric(value) ? (value > 0 ? 'up' : value < 0 ? 'down' : 'flat') : 'flat';
            const amount = numeric(value) ? this.formatter.format(Math.abs(value)) : '';
            const percentage = numeric(percent) ? `${percent < 0 ? '\u2212' : percent > 0 ? '+' : ''}${Math.abs(percent).toFixed(2)}%` : '';
            change.textContent = `${numeric(value) && value !== 0 ? (value > 0 ? '\u25b2 ' : '\u25bc ') : ''}${amount}  ${percentage}`;
            change.title = numeric(value) && numeric(percent)
              ? `Change from reference price${quote.referenceAt ? ' \u00b7 ' + new Date(quote.referenceAt).toLocaleString() : ''}`
              : 'Change unavailable';
          }
            if (manual && change) change.title = quote ? 'Chỉ số thay đổi được nhập thủ công' : 'Chỉ số thủ công không hợp lệ';
          return quote;
        });
        if (!this.isConnected) return;
        const failed = results.some((quote) => !quote || quote.stale);
        this.message(failed ? 'Một số giá chưa cập nhật được. Giá cũ được giữ kèm thời điểm cập nhật.' : '', failed ? 'Không lấy được một số giá. Vui lòng thử lại sau.' : '');
        const manualCount = items.filter((item) => item.dataset.manual === 'true').length;
        if (manualCount) {
          const note = this.querySelector('[data-editor]');
          if (note) note.textContent = items.some((item) => item.dataset.manual === 'true' && !this.manualQuote(item))
            ? 'Giá thủ công không hợp lệ. Dùng số và dấu chấm thập phân, giá không âm.'
            : manualCount + '/' + items.length + ' block đang dùng giá và chỉ số thủ công.';
        }
        this.repeat();
    }

    message(status, editor) {
      this.querySelector('[data-status]').textContent = status;
      const note = this.querySelector('[data-editor]');
      if (note) note.textContent = editor;
    }
  }
  customElements.define('gold-price-ticker', GoldPriceTicker);
})();
